import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { phaseKeys } from "src/api/Services/SharedSpace/phases";
import { taskKeys } from "src/api/Services/Tasks/tasks";
import { useAccessToken } from "src/api/useAccessToken";
import { useSnackBar } from "src/components/Reusable/CustomSnackbarProvider";
import { useRealmApp } from "src/store/RealmApp";

export const rolesKeys = {
  roles: () => ["roles"] as const,
};

export type RoleData = {
  _id: string;
  name: string;
  type: "internal" | "external";
  department?: {
    _id: string;
    name: string;
  };
  users: {
    _id: string;
    name: string;
    email: string;
  }[];
  instanceId: string;
  createdAt: string;
  updatedAt: string;
};

export const useGetRoles = () => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    rolesKeys.roles(),
    async (): Promise<RoleData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/roles`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser.customData.instanceId.$oid,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useCreateRole = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (role: {
      name: string;
      type: "internal" | "external";
      department?: {
        _id: string;
        name: string;
      };
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/role`,
        {
          instanceId: app.currentUser.customData.instanceId.$oid,
          ...role,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error creating role", "error");
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(rolesKeys.roles());
    },
  });
};

export const useUpdateRole = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (role: {
      _id: string;
      name?: string;
      department?: {
        _id: string;
        name: string;
      };
    }): Promise<{ message: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateRole`,
        {
          ...role,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error updating role", "error");
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(rolesKeys.roles());
    },
  });
};

export const useDeleteRole = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();

  return useMutation({
    mutationFn: async (_id: string): Promise<{ message: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.delete(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/role`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            _id,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error deleting role", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(rolesKeys.roles());
    },
  });
};

export const useAssignUserToTasksWithRoleInProject = () => {
  // const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (assignments: {
      phaseId: string;
      assignments: { role: { _id: string }; email: string }[];
    }): Promise<{ message: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/assignUsersToTasksWithRolesInProject`,
        assignments,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error assigning user to role", "error");
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(taskKeys.phaseTasks(variables.phaseId));
      queryClient.invalidateQueries(phaseKeys.phase(variables.phaseId));
      queryClient.invalidateQueries(["tasks"]);
      queryClient.invalidateQueries(["task"]);
    },
  });
};
